import { useEffect, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Switch, Divider } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiSwitch from "components/VuiSwitch";
import { PERMISSION_SCREENS } from "variables";
import { Formik } from "formik";
import { CreateRoleSchema } from "validations";
import useCommon from "hooks/useCommon";
import { POWER_LEVELS } from "variables";
import VuiAlert from "components/VuiAlert";
import OverlayLoader from "components/OverlayLoader";

const CreateRoleDialog = ({ open, onClose, onSave }) => {
  const { STORE, DISPATCH } = useCommon();
  const [screensEnabled, setScreensEnabled] = useState({});

  const hotelId = STORE((state) => state.ui.hotelId);
  const { createLoading, createError, createSuccess } = STORE((state) => state.roles);

  const permissionKey = (action, screen) => {
    if (typeof action === "object") {
      return action.key;
    }

    return `${action}${screen}`;
  };

  const calculateRoleLevel = (permissions) => {
    const enabledCount = Object.values(permissions || {}).filter(Boolean).length;
    const totalPossible = PERMISSION_SCREENS.length * 3;

    if (totalPossible === 0) return 1;

    const percentage = Math.round((enabledCount / totalPossible) * 100);

    // Clamp between 1 and 100
    return Math.min(100, Math.max(1, percentage));
  };

  const dialogPaperRef = useRef(null);

  const handleCancel = (resetForm) => {
    resetForm();
    onClose();
  };

  const handleSave = (values, resetForm) => {
    const level = calculateRoleLevel(values.permissions);

    const payload = {
      hotelId: values.hotelId,
      title: values.title,
      permissions: values.permissions,
      power: values.power,
      level,
    };

    onSave?.(payload);
    if (createSuccess) {
      resetForm();
    }
    // onClose();
  };

  useEffect(() => {
    if (createLoading) {
      // Scroll dialog content to top
      if (dialogPaperRef.current) {
        dialogPaperRef.current.scrollTop = 0;
      }

      // Lock page scroll
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [createLoading]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        ref: dialogPaperRef,
        sx: {
          overflow: createLoading ? "hidden" : "auto",
        },
      }}
    >
      <Formik
        initialValues={{
          title: "",
          screensEnabled: {},
          permissions: {},
          hotelId,
          power: POWER_LEVELS.ROLE_USER,
        }}
        validationSchema={CreateRoleSchema}
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, resetForm }) => {
          const toggleScreen = (screen, enabled) => {
            setFieldValue(`screensEnabled.${screen}`, enabled);

            const screenConfig = PERMISSION_SCREENS.find((s) => s.key === screen);

            if (!enabled) {
              const updated = { ...values.permissions };

              //  remove ALL actions
              screenConfig.actions.forEach((action) => {
                delete updated[permissionKey(action, screen)];
              });

              setFieldValue("permissions", updated);
            } else {
              const newPermissions = { ...values.permissions };

              // add permissions
              screenConfig.actions.forEach((action) => {
                const key = permissionKey(action, screen);
                const actionName = typeof action === "object" ? action.key : action;

                newPermissions[key] = actionName === "view";
              });

              setFieldValue("permissions", newPermissions);
            }
          };

          const togglePermission = (key, value) => {
            setFieldValue(`permissions.${key}`, value);
          };

          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle>
                <VuiTypography variant="h5">Create New Role</VuiTypography>
              </DialogTitle>

              <DialogContent>
                {createError && (
                  <VuiAlert color="error" dismissible size="sm">
                    {createError.message}
                  </VuiAlert>
                )}
                <VuiBox mb={2}>
                  <VuiTypography variant="caption" ml={1}>
                    Role Title
                  </VuiTypography>
                  <VuiInput
                    // textOnly
                    gradient
                    fullWidth
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    error={touched.title && !!errors.title}
                  />
                  {touched.title && errors.title && (
                    <VuiTypography variant="caption" color="error">
                      {errors.title}
                    </VuiTypography>
                  )}
                </VuiBox>

                <Divider />

                <VuiBox mt={2}>
                  <VuiTypography variant="button" mb={2}>
                    Permissions
                  </VuiTypography>

                  {PERMISSION_SCREENS.map(({ key, label, actions }) => (
                    <VuiBox key={key} mb={2}>
                      <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                        <VuiTypography variant="body2">{label}</VuiTypography>
                        <VuiSwitch
                          color="success"
                          checked={!!values.screensEnabled[key]}
                          onChange={(e) => toggleScreen(key, e.target.checked)}
                        />
                      </VuiBox>

                      {values.screensEnabled[key] && (
                        <VuiBox display="flex" gap={3} mt={1} pl={2}>
                          {actions.map((action) => {
                            const permKey = permissionKey(action, key);

                            return (
                              <VuiBox key={permKey} display="flex" alignItems="center" gap={1}>
                                <VuiSwitch
                                  color="success"
                                  checked={!!values.permissions[permKey]}
                                  onChange={(e) => togglePermission(permKey, e.target.checked)}
                                />
                                <VuiTypography variant="caption">
                                  <VuiTypography variant="caption">
                                    {typeof action === "object"
                                      ? action.label
                                      : action.charAt(0).toUpperCase() + action.slice(1)}
                                  </VuiTypography>
                                </VuiTypography>
                              </VuiBox>
                            );
                          })}
                        </VuiBox>
                      )}
                    </VuiBox>
                  ))}

                  {touched.permissions && errors.permissions && (
                    <VuiTypography variant="caption" color="error">
                      {errors.permissions}
                    </VuiTypography>
                  )}
                </VuiBox>
              </DialogContent>

              <DialogActions>
                <VuiButton
                  variant="outlined"
                  color="primary"
                  onClick={() => handleCancel(resetForm)}
                >
                  Cancel
                </VuiButton>
                <VuiButton
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={createLoading}
                >
                  {createLoading ? "Saving..." : "Create Role"}
                </VuiButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>
      <OverlayLoader open={createLoading} message="Creating new role..." />
    </Dialog>
  );
};

export default CreateRoleDialog;
